// ─────────────────────────────────────────────────────────────────────────────
// FACE/HEAD SEGMENTATION — MediaPipe Selfie Segmentation (face mask)
// Runtime: browser WASM via CDN (no server required)
// Degrades gracefully: if load fails, returns null mask
// ─────────────────────────────────────────────────────────────────────────────
import {
  ImageSegmenter,
  FilesetResolver,
  type ImageSegmenterResult,
} from "@mediapipe/tasks-vision";

export type { ImageSegmenterResult };

export let _faceSegmenter: ImageSegmenter | null = null;
let _faceSegLoading = false;
let _faceSegFailed = false;

export async function getFaceSegmenter(): Promise<ImageSegmenter | null> {
  if (_faceSegmenter) return _faceSegmenter;
  if (_faceSegFailed) return null;
  if (_faceSegLoading) {
    // Wait for existing load
    await new Promise<void>((res) => {
      const iv = setInterval(() => {
        if (!_faceSegLoading) {
          clearInterval(iv);
          res();
        }
      }, 100);
    });
    return _faceSegmenter;
  }

  _faceSegLoading = true;
  try {
    const vision = await FilesetResolver.forVisionTasks(
      "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
    );

    _faceSegmenter = await ImageSegmenter.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath:
          "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/1/selfie_segmenter.task",
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      outputCategoryMask: true,
      outputConfidenceMasks: false,
    });

    console.log("✅ Face Segmenter loaded");
    return _faceSegmenter;
  } catch (err) {
    console.warn("❌ Face Segmenter failed to load:", err);
    _faceSegFailed = true;
    return null;
  } finally {
    _faceSegLoading = false;
  }
}

export async function segmentFace(
  video: HTMLVideoElement
): Promise<ImageSegmenterResult | null> {
  const segmenter = await getFaceSegmenter();
  if (!segmenter) return null;

  try {
    const result = await segmenter.segmentForVideo(video, performance.now());
    return result;
  } catch (err) {
    console.warn("Face segmentation error:", err);
    return null;
  }
}

export function extractFaceMask(
  segmentationResult: ImageSegmenterResult
): ImageData | null {
  if (!segmentationResult.categoryMask) return null;

  // Convert segmentation mask to ImageData
  const mask = segmentationResult.categoryMask;
  const width = mask.width;
  const height = mask.height;

  // Create RGBA ImageData (mask is single channel)
  const imageData = new ImageData(width, height);
  const data = imageData.data;

  // Copy mask data (assuming category 1 = person/face)
  const maskData = mask.getAsUint8Array();
  for (let i = 0; i < maskData.length; i++) {
    const maskValue = maskData[i] / 255; // 0 or 1
    const pixelIndex = i * 4;

    // Set RGBA values (white for face, transparent for background)
    data[pixelIndex] = maskValue * 255;     // R
    data[pixelIndex + 1] = maskValue * 255; // G
    data[pixelIndex + 2] = maskValue * 255; // B
    data[pixelIndex + 3] = maskValue * 255; // A
  }

  return imageData;
}